import type { AnimDir, CardinalDir } from "./types";

const CARDINALS: CardinalDir[] = ["e", "n", "s", "w"];

/** Aim angle of each cardinal's sector center (screen y+ = south). */
const FACING_CENTER: Record<CardinalDir, number> = {
  e: 0,
  s: Math.PI / 2,
  w: Math.PI,
  n: -Math.PI / 2,
};

export function isCardinalDir(d: string): d is CardinalDir {
  return (CARDINALS as string[]).includes(d);
}

/**
 * Map aim radians to a cardinal facing.
 * Screen y+ is south (down the grid).
 */
export function facingDirFromAim(aimAngle: number): CardinalDir {
  const c = Math.cos(aimAngle);
  const s = Math.sin(aimAngle);
  if (Math.abs(c) >= Math.abs(s)) return c >= 0 ? "e" : "w";
  return s >= 0 ? "s" : "n";
}

/**
 * Cardinal facing with sector hysteresis: keep `current` until aim leaves an
 * expanded wedge (±45° + holdRad). Stops E↔W / N↔S flicker near 45° seams
 * when close-range aim chatters across a boundary.
 */
export function facingDirHeld(
  current: CardinalDir,
  aimAngle: number,
  holdRad = 0.28,
): CardinalDir {
  const desired = facingDirFromAim(aimAngle);
  if (desired === current) return current;
  const half = Math.PI / 4 + Math.max(0, holdRad);
  let delta = aimAngle - FACING_CENTER[current];
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;
  if (Math.abs(delta) <= half) return current;
  return desired;
}

/** Normalize optional facing for bake; side packs ignore and use default. */
export function resolveFacingDir(
  requested: AnimDir | undefined,
  defaultDir: AnimDir,
): AnimDir {
  if (requested == null) return defaultDir;
  if (requested === "side") return defaultDir === "side" ? "side" : defaultDir;
  return requested;
}
